import { toast } from '@approvalmax/ui/src/components';
import { produce } from 'immer';
import isEqual from 'lodash/isEqual';
import { domain } from 'modules/data';
import React, { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { messages } from './ReportSchedulerPopup.messages';
import { ReportSchedule } from './ReportSchedulerPopup.types';

export const initialValidationErrors: domain.ReportScheduleValidationErrors = {
    nameError: '',
    emailsError: '',
    weekError: '',
    longRuleName: false,
    emptyDay: false,
};

export const initialRuleData: domain.ReportScheduleRule = {
    name: messages.newRuleName,
    emails: [],
    validationErrors: initialValidationErrors,
    configuration: {
        scheduleOptions: domain.ScheduleOption.EveryWorkingDay,
        time: {
            hour: 8,
            minute: 0,
        },
        customSchedulePeriodicity: domain.CustomScheduleType.Weekly,
        dayOfMonth: 1,
        dayOfYear: {
            day: 0,
            month: 1,
        },
        daysOfWeek: [],
        monthlyOptions: domain.MonthlyOption.Day25th,
    },
};

function createRule(): domain.ReportScheduleRule {
    return initialRuleData;
}

interface ReportSchedulerContextType {
    schedule: ReportSchedule;
    updateRuleConfig: (ruleIndex: number, configFragment: Partial<domain.ReportScheduleRule['configuration']>) => void;
    updateRuleEmails: (ruleIndex: number, newEmails: string[]) => void;
    updateRuleName: (ruleIndex: number, name: string) => void;
    addNewRule: () => void;
    removeRule: (ruleIndex: number) => void;
    save: () => Promise<void>;
    loading: boolean;
}

const ReportSchedulerPopupContext = React.createContext<ReportSchedulerContextType>(null as any);

interface ReportSchedulerContextProviderProps extends PropsWithChildren {
    data: ReportSchedule;
    onSave: (data: ReportSchedule) => Promise<void>;
}

export const ReportSchedulerContextProvider: FC<ReportSchedulerContextProviderProps> = (props) => {
    const { children, data, onSave } = props;

    const [schedule, setSchedule] = useState(data);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (data.rules.length === 0) {
            setSchedule((schedule) => ({
                ...schedule,
                rules: [createRule()],
            }));
        }
    }, [data.rules.length]);

    const save = useCallback(async () => {
        let hasEmptyRequiredField = false;
        let someEqualConfigurations = false;
        let hasLongRuleName = false;

        const newSchedule = produce(schedule, (draft) => {
            draft.rules.forEach((rule, index) => {
                const {
                    emails,
                    name,
                    configuration: { customSchedulePeriodicity, daysOfWeek, scheduleOptions, dayOfYear },
                } = rule;

                if (emails.length === 0) {
                    hasEmptyRequiredField = true;
                    draft.rules[index].validationErrors.emailsError = messages.ruleEmailsError;
                }

                if (!name || !name.trim()) {
                    hasEmptyRequiredField = true;
                    draft.rules[index].validationErrors.nameError = messages.ruleNameError;
                }

                if (
                    scheduleOptions === domain.ScheduleOption.Custom &&
                    customSchedulePeriodicity === domain.CustomScheduleType.Weekly &&
                    !daysOfWeek?.length
                ) {
                    hasEmptyRequiredField = true;
                    draft.rules[index].validationErrors.weekError = messages.ruleWeekError;
                }

                if (customSchedulePeriodicity === domain.CustomScheduleType.Annually) {
                    if (!dayOfYear?.day) {
                        hasEmptyRequiredField = true;
                        draft.rules[index].validationErrors.emptyDay = true;
                    }
                }

                if (name.length > 100) {
                    hasLongRuleName = true;
                    draft.rules[index].validationErrors.longRuleName = true;
                } else {
                    draft.rules[index].validationErrors.longRuleName = false;
                }

                draft.reportConfigId = schedule.reportConfigId;
            });
        });

        if (hasEmptyRequiredField) {
            setSchedule(newSchedule);
            toast.error(messages.requireError);

            return;
        }

        if (hasLongRuleName) {
            setSchedule(newSchedule);
            toast.error(messages.ruleNameLengthError);

            return;
        }

        produce(schedule, (draft) => {
            draft.rules.forEach((rule) => {
                const restRules = draft.rules.filter((r) => r !== rule);

                restRules.find((r) => {
                    if (isEqual(r.emails.sort(), rule.emails.sort()) && isEqual(r.configuration, rule.configuration)) {
                        someEqualConfigurations = true;
                    }
                });
            });
        });

        if (someEqualConfigurations) {
            toast.error(messages.sameRuleError);

            return;
        }

        try {
            setLoading(true);
            await onSave(schedule);
        } finally {
            setLoading(false);
        }
    }, [schedule, onSave]);

    return (
        <ReportSchedulerPopupContext.Provider
            value={useMemo(
                () => ({
                    schedule,
                    updateRuleConfig(ruleIndex, configFragment) {
                        const newSchedule = produce(schedule, (draft) => {
                            draft.rules[ruleIndex].configuration = {
                                ...draft.rules[ruleIndex].configuration,
                                ...configFragment,
                            };
                            draft.rules[ruleIndex].validationErrors.weekError = '';
                        });

                        setSchedule(newSchedule);
                    },
                    updateRuleEmails(ruleIndex, newEmails) {
                        const newSchedule = produce(schedule, (draft) => {
                            draft.rules[ruleIndex].emails = newEmails;
                            draft.rules[ruleIndex].validationErrors.emailsError = '';
                        });

                        setSchedule(newSchedule);
                    },
                    updateRuleName(ruleIndex, newName) {
                        const newSchedule = produce(schedule, (draft) => {
                            draft.rules[ruleIndex].name = newName;
                            draft.rules[ruleIndex].validationErrors.nameError = '';
                        });

                        setSchedule(newSchedule);
                    },
                    addNewRule() {
                        const newSchedule = produce(schedule, (draft) => {
                            draft.rules.push(createRule());
                        });

                        setSchedule(newSchedule);
                    },
                    removeRule(ruleIndex) {
                        const newSchedule = produce(schedule, (draft) => {
                            draft.rules.splice(ruleIndex, 1);
                        });

                        setSchedule(newSchedule);
                    },
                    save,
                    loading,
                }),
                [loading, schedule, save]
            )}
        >
            {children}
        </ReportSchedulerPopupContext.Provider>
    );
};

export function useReportSchedulerContext() {
    return useContext(ReportSchedulerPopupContext);
}
